import { computed, inject, nextTick, useAttrs, type Ref } from 'vue'
import { useUiConfig } from '../components/config-provider/context'
import type { FormContext, FormItemContext } from '../components/form/types'
import type { ButtonSize } from '../types'

/** Shared form/config inheritance and native input attributes; values remain owned by each control. */
export function useFormControl(props: { disabled?: boolean; size?: ButtonSize; status?: 'error' | 'warning' }) {
  const config = useUiConfig()
  const form = inject<Ref<FormContext> | null>('rrFormContext', null)
  const item = inject<FormItemContext | null>('rrFormItemContext', null)
  const attrs = useAttrs()
  const disabled = computed(() => props.disabled ?? form?.value.disabled ?? config.value.componentDisabled ?? false)
  const size = computed(() => props.size ?? form?.value.size ?? config.value.componentSize ?? 'middle')
  const status = computed(() => props.status ?? item?.validateStatus.value)
  // useAttrs values are updated by Vue but are not reactive computed dependencies.
  function controlAttrs() {
    const { class: _class, style: _style, ...nativeAttrs } = attrs
    return {
      id: item?.id.value,
      'aria-invalid': status.value === 'error' || undefined,
      'aria-describedby': item?.helpId.value,
      ...nativeAttrs
    }
  }
  function onFieldChange() { return nextTick(() => item?.onFieldChange()) }
  function onFieldBlur() { return item?.onFieldBlur() }
  return { config, attrs, disabled, size, status, controlAttrs, onFieldChange, onFieldBlur }
}
